import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { joinPublicStorefrontUrl } from "../api/client";
import { getCanonicalSiteOrigin } from "../utils/siteUrl";

const SiteSeoContext = createContext(null);

function defaultsFromEnv() {
  const orgUrl = (
    process.env.REACT_APP_ORGANIZATION_URL ||
    process.env.REACT_APP_SITE_URL ||
    ""
  ).trim();

  let sameFromEnv = [];
  try {
    const raw = process.env.REACT_APP_ORGANIZATION_SAME_AS;
    if (raw && String(raw).trim())
      sameFromEnv = String(raw)
        .split(/[\n\r,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
  } catch (_) {
    sameFromEnv = [];
  }

  return {
    google_site_verification: (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "").trim(),
    bing_site_verification: (process.env.REACT_APP_BING_SITE_VERIFICATION || "").trim(),
    twitter_handle: (process.env.REACT_APP_TWITTER_HANDLE || "").trim().replace(/^@+/, ""),
    default_og_image_url: (process.env.REACT_APP_DEFAULT_OG_IMAGE || "").trim(),
    organization_name: (process.env.REACT_APP_ORGANIZATION_NAME || "Gem Fashion Studios").trim(),
    organization_logo_url: (process.env.REACT_APP_ORGANIZATION_LOGO_URL || "").trim(),
    organization_url: orgUrl.replace(/\/+$/, "") || getCanonicalSiteOrigin(),
    same_as: sameFromEnv.length ? sameFromEnv : null,
    loadedFromApi: false,
  };
}

function mergePreferApi(base, api) {
  if (!api || typeof api !== "object") return base;
  const out = { ...base };
  const pick = (k) => {
    const v = api[k];
    if (v !== null && v !== undefined && String(v).trim() !== "") out[k] = typeof v === "string" ? v.trim() : v;
  };
  pick("google_site_verification");
  pick("bing_site_verification");
  pick("twitter_handle");
  pick("default_og_image_url");
  pick("organization_name");
  pick("organization_logo_url");
  pick("organization_url");
  if (Array.isArray(api.same_as) && api.same_as.length) out.same_as = api.same_as.map((x) => String(x).trim()).filter(Boolean);
  out.loadedFromApi = true;
  return out;
}

export function SiteSeoProvider({ children }) {
  const envDefaults = useMemo(() => defaultsFromEnv(), []);
  const [config, setConfig] = useState(envDefaults);

  useEffect(() => {
    let cancelled = false;
    fetch(joinPublicStorefrontUrl("/api/site-seo"), { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled || !json || typeof json !== "object") return;
        const handle = json.twitter_handle != null ? String(json.twitter_handle).replace(/^@+/, "") : "";
        setConfig((prev) =>
          mergePreferApi(prev, {
            google_site_verification: json.google_site_verification,
            bing_site_verification: json.bing_site_verification,
            twitter_handle: handle || undefined,
            default_og_image_url: json.default_og_image_url,
            organization_name: json.organization_name,
            organization_logo_url: json.organization_logo_url,
            organization_url: json.organization_url,
            same_as: json.same_as,
          })
        );
      })
      .catch(() => {})
      .finally(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => config, [config]);
  return <SiteSeoContext.Provider value={value}>{children}</SiteSeoContext.Provider>;
}

export function useSiteSeo() {
  const ctx = useContext(SiteSeoContext);
  if (!ctx) return defaultsFromEnv();
  return ctx;
}
